import React, { useMemo, useRef } from 'react';
import { motion, AnimatePresence, useSpring, useAnimationFrame, useTransform } from 'framer-motion';
import { karaokeLyrics } from '../../config/karaokeConfig';

// Переключатель режима: 
// true = реакция на бас (анализ звука в реальном времени)
// false = старый режим (просто цикличная анимация, не привязанная к музыке)
const ENABLE_BEAT_DETECTION = false;

const KaraokeDisplay = ({ lyrics: passedLyrics = [], currentTime, currentTrack, analyser, beatParams, accentColor = 'purple' }) => {
    // 1. Конфигурация
    const beatConfig = useMemo(() => ({
        duration: 0.8,
        scale: beatParams?.scaleConfig ?? 1.3,
        yOffset: -5,
        ease: "easeInOut"
    }), [beatParams?.scaleConfig]);

    // 2. Базовая мемоизация данных
    const lyrics = useMemo(() => {
        if (passedLyrics && passedLyrics.length > 0) return passedLyrics;
        if (!currentTrack) return [];
        return karaokeLyrics[currentTrack] || karaokeLyrics["default"] || [];
    }, [passedLyrics, currentTrack]);

    const currentLineIndex = useMemo(() => {
        for (let i = lyrics.length - 1; i >= 0; i--) {
            if (currentTime >= lyrics[i].time) return i;
        }
        return -1;
    }, [lyrics, currentTime]);

    const displayData = useMemo(() => {
        if (!currentTrack) return { text: "", isIntro: false };
        if (currentLineIndex !== -1) return { text: lyrics[currentLineIndex].text, isIntro: false };
        const introDuration = lyrics.length > 0 ? (lyrics[0].time || 5) : 10;
        if (currentTime < introDuration || lyrics.length === 0) return { text: currentTrack, isIntro: true };
        return { text: currentTrack, isIntro: true };
    }, [lyrics, currentLineIndex, currentTime, currentTrack]);

    const accentGlows = {
        purple: 'rgba(192,38,211,0.55)',
        blue: 'rgba(59,130,246,0.55)',
        orange: 'rgba(249,115,22,0.55)',
        red: 'rgba(239,68,68,0.55)',
        green: 'rgba(16,185,129,0.55)',
        white: 'rgba(255,255,255,0.7)',
        cyan: 'rgba(6,182,212,0.55)',
    };
    const currentGlow = accentGlows[accentColor] || accentGlows.purple;

    const accentGradients = {
        purple: 'linear-gradient(90deg, #ffffff 0%, #f0abfc 25%, #c026d3 50%, #e879f9 75%, #ffffff 100%)',
        blue: 'linear-gradient(90deg, #ffffff 0%, #93c5fd 25%, #3b82f6 50%, #60a5fa 75%, #ffffff 100%)',
        orange: 'linear-gradient(90deg, #ffffff 0%, #fed7aa 25%, #f97316 50%, #fb923c 75%, #ffffff 100%)',
        red: 'linear-gradient(90deg, #ffffff 0%, #fca5a5 25%, #ef4444 50%, #f87171 75%, #ffffff 100%)',
        green: 'linear-gradient(90deg, #ffffff 0%, #a7f3d0 25%, #10b981 50%, #34d399 75%, #ffffff 100%)',
        white: 'linear-gradient(90deg, #ffffff 0%, #cbd5e1 30%, #ffffff 50%, #e2e8f0 70%, #ffffff 100%)',
        cyan: 'linear-gradient(90deg, #ffffff 0%, #a5f3fc 25%, #06b6d4 50%, #22d3ee 75%, #ffffff 100%)',
    };
    const currentGradient = accentGradients[accentColor] || accentGradients.purple;

    // 3. Пружины для физики — высокая stiffness для мгновенного отклика на удар
    const springScale = useSpring(1, { damping: 8, stiffness: 300 });
    const springBrightness = useSpring(1.1, { damping: 10, stiffness: 250 });
    const springY = useSpring(0, { damping: 10, stiffness: 200 });
    const brightnessFilter = useTransform(springBrightness, (v) => `brightness(${v})`);

    // Beat detection refs (onset detection через spectral flux)
    const prevFreqDataRef = useRef(null);
    const fluxHistoryRef = useRef([]);
    const beatIntensityRef = useRef(0);
    const lastBeatFrameRef = useRef(0);
    const frameCountRef = useRef(0);

    // 4. Анимационный цикл с beat detection (onset detection через spectral flux)
    useAnimationFrame(() => {
        // Если условий для анализа нет или бит отключен, возвращаем пружины в дефолт
        if (!ENABLE_BEAT_DETECTION || !analyser || displayData.isIntro || !currentTrack || !displayData.text) {
            if (springScale.get() !== 1) springScale.set(1);
            if (springY.get() !== 0) springY.set(0);
            if (springBrightness.get() !== 1.1) springBrightness.set(1.1);
            return;
        }

        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);
        analyser.getByteFrequencyData(dataArray);

        // === Шаг 1: Энергия басов ===
        const bassEnd = Math.min(beatParams?.bassEnd ?? 12, bufferLength);
        let currentBassEnergy = 0;
        for (let i = 0; i < bassEnd; i++) currentBassEnergy += dataArray[i];
        currentBassEnergy /= (bassEnd * 255);

        // === Шаг 2: Spectral flux — считаем только положительные изменения (onset) ===
        let spectralFlux = 0;
        const prevData = prevFreqDataRef.current;
        if (prevData) {
            for (let i = 0; i < bassEnd; i++) {
                const diff = dataArray[i] - prevData[i];
                if (diff > 0) spectralFlux += diff; // Только рост энергии = начало удара
            }
            spectralFlux /= (bassEnd * 255);
        }

        // Сохраняем текущий кадр для сравнения в следующем
        if (!prevFreqDataRef.current) {
            prevFreqDataRef.current = new Uint8Array(bufferLength);
        }
        prevFreqDataRef.current.set(dataArray);

        // === Шаг 3: Адаптивный порог через скользящее среднее (~0.7 сек) ===
        const history = fluxHistoryRef.current;
        history.push(spectralFlux);
        if (history.length > 43) history.shift(); // 43 кадра ≈ 0.7с при 60fps

        const avgFlux = history.length > 0
            ? history.reduce((sum, v) => sum + v, 0) / history.length
            : 0;

        frameCountRef.current++;

        // === Шаг 4: Beat detection — flux значительно выше среднего ===
        const cooldownFrames = beatParams?.cooldownFrames ?? 10;
        const framesSinceLastBeat = frameCountRef.current - lastBeatFrameRef.current;

        const isBeat = spectralFlux > Math.max(avgFlux * (beatParams?.thresholdMultiplier ?? 1.5), beatParams?.absoluteThreshold ?? 0.04) && framesSinceLastBeat > cooldownFrames;

        // === Шаг 5: Интенсивность удара с экспоненциальным затуханием ===
        let intensity = beatIntensityRef.current;
        if (isBeat) {
            // На удар: резкий скачок, сила зависит от громкости баса
            const multi = beatParams?.intensityMultiplier ?? 0.6;
            intensity = Math.min(1.0, multi + currentBassEnergy * multi);
            lastBeatFrameRef.current = frameCountRef.current;
        } else {
            // Между ударами: плавное затухание (~300ms до нуля)
            intensity *= beatParams?.decay ?? 0.9;
        }
        if (intensity < 0.01) intensity = 0;
        beatIntensityRef.current = intensity;

        // === Шаг 6: Обновляем пружины ===
        springScale.set(1 + intensity * (beatConfig.scale - 1) * 1.8);
        springY.set(intensity * beatConfig.yOffset * 1.5);
        springBrightness.set(1.1 + intensity * 0.6);
    });

    // 5. Проверка рендеринга (Early return только ПОСЛЕ всех хуков)
    if (!currentTrack || !displayData.text) return <div className="min-h-[80px]" />;

    const isRealtime = ENABLE_BEAT_DETECTION && !!analyser && !displayData.isIntro;

    return (
        <div className="w-full flex flex-col items-center justify-center py-4 min-h-[100px] relative">
            <motion.div
                className="w-full flex justify-center items-center pointer-events-auto overflow-visible"
                style={isRealtime ? {
                    scale: springScale,
                    y: springY,
                    filter: brightnessFilter
                } : {}}
                animate={!isRealtime ? {
                    scale: [1, beatConfig.scale, 1],
                    y: [0, beatConfig.yOffset, 0],
                    filter: ['brightness(1.1)', 'brightness(1.3)', 'brightness(1.1)']
                } : undefined}
                transition={!isRealtime ? {
                    duration: beatConfig.duration,
                    repeat: Infinity,
                    ease: beatConfig.ease
                } : undefined}
            >
                <AnimatePresence mode="popLayout">
                    <motion.div
                        key={displayData.isIntro ? 'intro' : (currentLineIndex + displayData.text)}
                        initial={{ opacity: 0, y: 30, filter: 'blur(12px)' }}
                        animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                        exit={{ opacity: 0, y: -30, filter: 'blur(12px)' }}
                        transition={{
                            duration: 0.45,
                            ease: [0.23, 1, 0.32, 1]
                        }}
                        className="karaoke-text text-center font-black max-w-full sm:max-w-4xl px-2 sm:px-4 select-text cursor-text leading-snug tracking-wide break-words"
                        style={{
                            fontSize: displayData.isIntro ? 'clamp(1.1rem, 2.5vw, 1.8rem)' : 'clamp(1.35rem, 3.2vw, 2.4rem)',
                            filter: displayData.isIntro
                                ? `drop-shadow(0 0 10px ${currentGlow})`
                                : `drop-shadow(0 0 14px ${currentGlow}) drop-shadow(0 0 28px ${currentGlow})`,
                            textShadow: `0 0 24px ${currentGlow}`,
                            background: currentGradient,
                            backgroundSize: '100% auto',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            textTransform: displayData.isIntro ? 'uppercase' : 'none',
                            letterSpacing: displayData.isIntro ? '0.2em' : 'normal',
                            willChange: 'transform, opacity, filter'
                        }}
                    >
                        {displayData.text}
                    </motion.div>
                </AnimatePresence>
            </motion.div>

            <style>{`
                .karaoke-text::selection {
                    background: rgba(255, 255, 255, 0.35);
                    -webkit-text-fill-color: #ffffff;
                    color: #ffffff;
                }
            `}</style>
        </div>
    );
};

export default KaraokeDisplay;
